import { useEffect, useRef, useCallback, useMemo } from "react";
import { useRouteStore } from "../store/useRouteStore";
import { RouteProcessor } from "../utils/routeUtils";
import type { PlaybackFrame } from "../types/route";

export function useRoutePlayer() {
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const { selectedRoute, playerState, seekTo, stopRoute } = useRouteStore();

  const playbackFrames = useMemo<PlaybackFrame[]>(() => {
    if (!selectedRoute) return [];
    return RouteProcessor.buildPlaybackFrames(selectedRoute);
  }, [selectedRoute]);

  const updateProgress = useCallback(() => {
    if (
      !selectedRoute ||
      !playerState.isPlaying ||
      playbackFrames.length === 0
    ) {
      return;
    }

    const { currentIndex } = playerState;

    if (currentIndex >= playbackFrames.length - 1) {
      stopRoute();
      return;
    }

    const newIndex = currentIndex + 1;
    const progress =
      playbackFrames.length > 1
        ? (newIndex / (playbackFrames.length - 1)) * 100
        : 100;
    const frame = playbackFrames[newIndex];
    const position = RouteProcessor.getFramePosition(frame);

    useRouteStore.setState((state) => ({
      playerState: {
        ...state.playerState,
        currentIndex: newIndex,
        progress,
        currentTime: position.timestamp,
      },
    }));
  }, [selectedRoute, playerState, playbackFrames, stopRoute]);

  useEffect(() => {
    if (playerState.isPlaying && selectedRoute) {
      const interval = 1000 / playerState.playbackSpeed; // Adjust for playback speed
      intervalRef.current = setInterval(updateProgress, interval);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    playerState.isPlaying,
    playerState.playbackSpeed,
    updateProgress,
    selectedRoute,
  ]);

  const getCurrentFrame = useCallback((): PlaybackFrame | null => {
    if (playbackFrames.length === 0) return null;
    return playbackFrames[playerState.currentIndex] ?? null;
  }, [playbackFrames, playerState.currentIndex]);

  const getCurrentPosition = useCallback(() => {
    const frame = getCurrentFrame();
    if (!frame) return null;

    const position = RouteProcessor.getFramePosition(frame);
    return {
      latitude: position.latitude,
      longitude: position.longitude,
      timestamp: position.timestamp,
      speed: position.speed,
      battery_level: position.battery_level,
      isInTransit: position.isInTransit,
      tripIndex: position.tripIndex,
      gapReason: position.gapReason,
    };
  }, [getCurrentFrame]);

  const jumpToTime = useCallback(
    (targetTime: string) => {
      if (!selectedRoute || playbackFrames.length === 0) return;

      const targetTimestamp = new Date(targetTime).getTime();
      let closestIndex = 0;
      let closestDiff = Infinity;

      playbackFrames.forEach((frame, index) => {
        const position = RouteProcessor.getFramePosition(frame);
        const coordTime = new Date(position.timestamp).getTime();
        const diff = Math.abs(coordTime - targetTimestamp);

        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = index;
        }
      });

      const progress =
        playbackFrames.length > 1
          ? (closestIndex / (playbackFrames.length - 1)) * 100
          : 0;
      seekTo(progress);
    },
    [selectedRoute, playbackFrames, seekTo],
  );

  const getPlaybackInfo = useCallback(() => {
    if (!selectedRoute) return null;

    const totalDuration = selectedRoute.duration_minutes;
    const currentProgress = playerState.progress / 100;
    const currentDuration = totalDuration * currentProgress;
    const remainingDuration = totalDuration - currentDuration;
    const frame = getCurrentFrame();

    return {
      totalDuration,
      currentDuration,
      remainingDuration,
      totalPoints: playbackFrames.length,
      currentPoint: playerState.currentIndex,
      currentFrame: frame,
    };
  }, [selectedRoute, playerState, playbackFrames, getCurrentFrame]);

  return {
    playbackFrames,
    getCurrentFrame,
    getCurrentPosition,
    jumpToTime,
    getPlaybackInfo,
  };
}
